import { Pencil, Trash2 } from 'lucide-react';
import type { TrackPermissionRow, TrackRole } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

/**
 * The roles × permissions grid (HUB-109 D4): rows are the track's permissions
 * — manifest rows first, then one row per copilot tool — columns are its
 * roles, a cell is one checkbox. Pure and presentational; `RolesTab` owns the
 * data and the optimistic writes.
 */

export const APP_PERMISSIONS_HEADING = 'App permissions — declared by the track in trackframe.config.json';
export const COPILOT_TOOLS_HEADING = 'Copilot tools — one row per tool the track exposes to the copilot';
export const NO_ROLES_TEXT = 'No roles yet. Add one to start.';
export const NO_PERMISSIONS_TEXT = 'This track declares no permissions and exposes no copilot tools.';
export const NO_CUSTOM_PERMISSIONS_TEXT = 'This track declares no app permissions.';
export const NO_TOOLS_TEXT = 'This track exposes no copilot tools.';

export interface PermissionGroups {
  custom: TrackPermissionRow[];
  tools: TrackPermissionRow[];
}

/** Manifest rows before tool rows — the order both the matrix and the role form use. */
export function splitPermissions(rows: readonly TrackPermissionRow[]): PermissionGroups {
  return {
    custom: rows.filter((row) => row.kind === 'custom'),
    tools: rows.filter((row) => row.kind === 'tool'),
  };
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** Label cell shared by the matrix rows; a tool row carries its read/write badge. */
export function PermissionLabel({ row }: { row: TrackPermissionRow }) {
  return (
    <div>
      <div className="flex items-center gap-2 font-medium">
        {row.label}
        {row.toolKind && (
          <Badge variant={row.toolKind === 'write' ? 'default' : 'outline'}>{row.toolKind}</Badge>
        )}
      </div>
      <div className="font-mono text-xs text-muted-foreground">{row.name}</div>
      {row.description && <div className="text-xs text-muted-foreground">{row.description}</div>}
    </div>
  );
}

export type GroupState = 'all' | 'some' | 'none';

/** How much of a group one role holds — drives the select-all checkbox. */
export function groupState(role: TrackRole, names: readonly string[]): GroupState {
  const held = names.filter((name) => role.permissions.includes(name)).length;
  if (held === 0) return 'none';
  return held === names.length ? 'all' : 'some';
}

interface GroupRowProps {
  heading: string;
  roles: TrackRole[];
  rows: TrackPermissionRow[];
  busy?: boolean;
  onSetPermissions: RolesMatrixProps['onSetPermissions'];
}

/** Group heading row; each role column carries a select-all for the group. */
function GroupRow({ heading, roles, rows, busy, onSetPermissions }: GroupRowProps) {
  const names = rows.map((row) => row.name);
  return (
    <TableRow className="bg-muted/50 hover:bg-muted/50">
      <TableCell className="py-1.5 text-[11px] font-semibold uppercase tracking-wider">{heading}</TableCell>
      {roles.map((role) => {
        const state = groupState(role, names);
        return (
          <TableCell key={role.id} className="py-1.5 text-center">
            {names.length > 0 && (
              <Checkbox
                aria-label={`${role.name}: select all in ${heading}`}
                title="Select all"
                checked={state === 'all'}
                indeterminate={state === 'some'}
                disabled={busy}
                onCheckedChange={() => onSetPermissions(role, names, state !== 'all')}
              />
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

export interface RolesMatrixProps {
  roles: TrackRole[];
  permissions: TrackPermissionRow[];
  /** Disables every cell while a write is in flight. */
  busy?: boolean;
  error?: string | null;
  /** Tick (`checked`) or untick every name — one cell or a whole group. */
  onSetPermissions: (role: TrackRole, names: string[], checked: boolean) => void;
  onAddRole: () => void;
  onEditRole: (role: TrackRole) => void;
  onDeleteRole: (role: TrackRole) => void;
}

export function RolesMatrix({
  roles,
  permissions,
  busy,
  error,
  onSetPermissions,
  onAddRole,
  onEditRole,
  onDeleteRole,
}: RolesMatrixProps) {
  const groups = splitPermissions(permissions);
  const colSpan = roles.length + 1;

  const permissionRow = (row: TrackPermissionRow) => (
    <TableRow key={row.name}>
      <TableCell className="min-w-[260px]">
        <PermissionLabel row={row} />
      </TableCell>
      {roles.map((role) => (
        <TableCell key={role.id} className="w-[120px] text-center">
          <Checkbox
            id={`${role.id}:${row.name}`}
            aria-label={`${role.name} has ${row.name}`}
            checked={role.permissions.includes(row.name)}
            disabled={busy}
            onCheckedChange={(checked) => onSetPermissions(role, [row.name], checked)}
          />
        </TableCell>
      ))}
    </TableRow>
  );

  const emptyRow = (text: string) => (
    <TableRow>
      <TableCell colSpan={colSpan} className="text-sm text-muted-foreground">
        {text}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {plural(roles.length, 'role')} · {plural(groups.custom.length, 'app permission')} ·{' '}
          {plural(groups.tools.length, 'copilot tool')}
        </p>
        <Button size="sm" onClick={onAddRole}>
          Add role
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {roles.length === 0 && <p className="text-sm text-muted-foreground">{NO_ROLES_TEXT}</p>}

      {permissions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{NO_PERMISSIONS_TEXT}</p>
      ) : (
        <Table aria-label="Roles by permissions">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[260px]">Permission</TableHead>
              {roles.map((role) => (
                <TableHead key={role.id} className="h-auto min-w-[120px] py-2 text-center normal-case tracking-normal">
                  <div className="text-sm font-semibold text-foreground">{role.name}</div>
                  <div className="flex items-center justify-center gap-1 text-xs font-normal">
                    <span className="whitespace-nowrap">{plural(role.memberCount, 'user')}</span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-6"
                      aria-label={`Edit ${role.name}`}
                      title="Edit role"
                      onClick={() => onEditRole(role)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-6"
                      aria-label={`Delete ${role.name}`}
                      title="Delete role"
                      onClick={() => onDeleteRole(role)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <GroupRow
              heading={APP_PERMISSIONS_HEADING}
              roles={roles}
              rows={groups.custom}
              busy={busy}
              onSetPermissions={onSetPermissions}
            />
            {groups.custom.length > 0 ? groups.custom.map(permissionRow) : emptyRow(NO_CUSTOM_PERMISSIONS_TEXT)}
            <GroupRow
              heading={COPILOT_TOOLS_HEADING}
              roles={roles}
              rows={groups.tools}
              busy={busy}
              onSetPermissions={onSetPermissions}
            />
            {groups.tools.length > 0 ? groups.tools.map(permissionRow) : emptyRow(NO_TOOLS_TEXT)}
          </TableBody>
        </Table>
      )}

      <p className="text-xs text-muted-foreground">
        A tick means the role holds the permission. Untick removes it at once. Levels are separate: a{' '}
        <b>viewer</b> already has every read tool and a <b>member</b> every read and write tool, whatever is
        ticked here.
      </p>
      <div className="rounded-[var(--radius-ctl)] border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
        <b>Permissions come from code.</b> A developer adds an app permission in{' '}
        <code className="font-mono text-xs">trackframe.config.json</code>; copilot tool rows come from the
        track&apos;s OpenAPI spec. There is no way to add one here. If a developer renames a copilot tool,
        its ticks are lost and must be set again.
      </div>
    </div>
  );
}
